import React from 'react';
import type { InvoiceTemplateConfig } from './invoiceTemplateConfig';
import { escapeHtml, fragmentToSafeHtml, payToBlockSafeHtml } from './invoiceTemplateHtmlUtils';

type Props = {
  config: InvoiceTemplateConfig;
  /** Bump to re-run layout after image load */
  refreshKey?: number;
};

/** Demo customer / line items only — pay-to & thank-you come from template config */
const SAMPLE = {
  billToName: 'Patentskart Consultants Private Limited',
  billToLines: 'Plot No. 10, B1,\nNetsmartz House, Chandigarh\nIndia',
  gstin: '04AANCP0752E1ZN',
  placeOfSupply: 'Chandigarh (04)',
  invoiceNo: 'INV-SWS-1702187',
  invoiceDate: '07/03/2026',
  terms: 'Due on Receipt',
  dueDate: '07/03/2026',
  rows: [
    {
      no: 1,
      desc: 'AI Service\nProfessional Charges for Managing CRM,\nProviding AI and Calling services — SAC: 998314',
      qty: '1.00',
      rate: '40,000.00',
      cgst: '3,600.00',
      cgstPct: '9%',
      ugst: '3,600.00',
      ugstPct: '9%',
      amount: '40,000.00',
    },
  ],
  subTotal: '40,000.00',
  cgstLbl: 'CGST (9%)',
  cgstAmt: '3,600.00',
  ugstLbl: 'UTGST (9%)',
  ugstAmt: '3,600.00',
  total: '₹47,200.00',
  totalWords: 'Rupees Forty-Seven Thousand Two Hundred Only',
};

function headerFallbackStyle(cfg: InvoiceTemplateConfig): React.CSSProperties {
  const h = cfg.header_footer.header;
  const minPx = Math.max(72, cfg.header_footer.header.header_height_first_page_in * 24);
  return {
    minHeight: `${minPx}px`,
    backgroundColor: h.use_background_color ? h.background_color : 'transparent',
    backgroundImage: `linear-gradient(105deg, ${cfg.primary_color} 0%, #f97316 45%, #fecaca 100%)`,
  };
}

function headerImageSrc(cfg: InvoiceTemplateConfig): string {
  const h = cfg.header_footer.header;
  return h.image_data_url || h.image_url || cfg.logo_url || '';
}

function pageBgStyle(cfg: InvoiceTemplateConfig): React.CSSProperties {
  const b = cfg.general.background;
  const style: React.CSSProperties = {
    backgroundColor: b.use_background_color ? b.background_color : '#ffffff',
  };
  const src = b.image_data_url || b.image_url;
  if (src) {
    style.backgroundImage = `url(${src})`;
    style.backgroundSize = 'cover';
    style.backgroundRepeat = 'no-repeat';
    style.backgroundPosition = b.image_position.replace(/_/g, ' ');
  }
  return style;
}

export const InvoiceTemplatePreview: React.FC<Props> = ({ config, refreshKey = 0 }) => {
  const paid = config.show_invoice_as_paid;
  const td = config.transaction_details;
  const tbl = config.table;
  const tot = config.total;
  const g = config.general.font;
  const labelStyle: React.CSSProperties = {
    color: g.label_color,
    fontSize: `${g.font_size_pt}pt`,
    fontFamily: g.pdf_font,
  };
  const bodyStyle: React.CSSProperties = {
    color: g.font_color,
    fontSize: `${g.font_size_pt}pt`,
    fontFamily: g.pdf_font,
  };

  return (
    <div
      key={refreshKey}
      className="mx-auto max-h-[min(85vh,920px)] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-inner"
      style={{
        ...pageBgStyle(config),
        fontFamily: `${config.general.font.pdf_font}, system-ui, sans-serif`,
      }}
    >
      <div className="p-3 sm:p-4">
        <div className="overflow-hidden rounded-md border border-slate-200/80 bg-white/95 shadow-sm">
          {headerImageSrc(config) ? (
            <div
              className="relative w-full"
              style={{
                backgroundColor: config.header_footer.header.use_background_color
                  ? config.header_footer.header.background_color
                  : undefined,
              }}
            >
              {/* Full image visible: scale to preview width, height follows aspect ratio (no cover/crop). */}
              <img
                src={headerImageSrc(config)}
                alt=""
                className="block h-auto w-full max-w-full bg-transparent"
                decoding="async"
              />
              {config.company_legal_name?.trim() ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent px-3 pb-1 pt-8">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-white drop-shadow-md">
                    {config.company_legal_name}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              style={headerFallbackStyle(config)}
              className="relative flex min-h-[72px] items-end px-3 pb-1 pt-2"
            >
              <div className="text-[10px] font-bold uppercase tracking-wide text-white drop-shadow-md">
                {config.company_legal_name || ' '}
              </div>
            </div>
          )}

          {config.header_footer.header.header_content_note?.trim() ? (
            <div
              className="border-b border-slate-200 bg-slate-50/90 px-3 py-1.5 text-[7pt] text-slate-800 sm:text-[8pt]"
              style={bodyStyle}
            >
              {config.header_footer.header.header_content_note}
            </div>
          ) : null}

          <div className="px-3 py-2" style={bodyStyle}>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 pb-2">
              <div>
                <p style={{ ...labelStyle, fontSize: `${Math.max(6, config.general.font.font_size_pt - 1)}pt` }}>
                  {td.bill_to_heading}
                </p>
                <p className="font-semibold leading-snug text-slate-900">{SAMPLE.billToName}</p>
                <p className="whitespace-pre-line text-slate-700">{SAMPLE.billToLines}</p>
                <p className="mt-1 text-slate-700">{config.tax_id_label}: {SAMPLE.gstin}</p>
                <p className="text-slate-700">
                  {td.place_of_supply_label}: {SAMPLE.placeOfSupply}
                </p>
              </div>
              <div className="text-right">
                {paid ? (
                  <div className="mb-1 flex justify-end">
                    <span className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                      {config.paid_badge_label || 'PAID'}
                    </span>
                  </div>
                ) : null}
                <p
                  className="text-lg font-bold tracking-tight"
                  style={{ color: config.primary_color }}
                >
                  {config.invoice_title}
                </p>
                <p style={labelStyle}>{td.invoice_number_label}</p>
                <p className="font-mono font-semibold text-slate-900">{SAMPLE.invoiceNo}</p>
              </div>
            </div>

            <div className="mb-2 grid grid-cols-2 gap-x-4 gap-y-1 text-slate-800 sm:grid-cols-3">
              <div>
                <span style={labelStyle}>{td.invoice_date_label}</span>
                <span className="ml-1">{SAMPLE.invoiceDate}</span>
              </div>
              <div>
                <span style={labelStyle}>{paid ? td.payment_status_label : td.terms_label}</span>
                <span className="ml-1">{paid ? td.paid_in_full_text : SAMPLE.terms}</span>
              </div>
              <div>
                <span style={labelStyle}>{td.due_date_label}</span>
                <span className="ml-1">{paid ? td.due_date_paid_placeholder : SAMPLE.dueDate}</span>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-300">
              <table
                className={`w-full border-collapse text-left text-[7pt] sm:text-[8pt] ${
                  tbl.show_second_tax_column ? 'min-w-[500px]' : 'min-w-[380px]'
                }`}
              >
                <thead>
                  <tr className="border-b border-slate-400 bg-slate-100" style={labelStyle}>
                    <th className="border-r border-slate-300 px-1 py-1">{tbl.col_no}</th>
                    <th className="border-r border-slate-300 px-1 py-1">{tbl.col_description}</th>
                    <th className="border-r border-slate-300 px-1 py-1">{tbl.col_qty}</th>
                    <th className="border-r border-slate-300 px-1 py-1">{tbl.col_rate}</th>
                    <th className="border-r border-slate-300 px-1 py-1">{tbl.col_cgst}</th>
                    {tbl.show_second_tax_column ? (
                      <th className="border-r border-slate-300 px-1 py-1">{tbl.col_second_tax}</th>
                    ) : null}
                    <th className="px-1 py-1">{tbl.col_amount}</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE.rows.map((r) => (
                    <tr key={r.no} className="border-b border-slate-200 align-top">
                      <td className="border-r border-slate-200 px-1 py-1">{r.no}</td>
                      <td className="border-r border-slate-200 whitespace-pre-line px-1 py-1">{r.desc}</td>
                      <td className="border-r border-slate-200 px-1 py-1">{r.qty}</td>
                      <td className="border-r border-slate-200 px-1 py-1">{r.rate}</td>
                      <td className="border-r border-slate-200 px-1 py-1">
                        {r.cgst}
                        <div className="text-[90%] opacity-80">{r.cgstPct}</div>
                      </td>
                      {tbl.show_second_tax_column ? (
                        <td className="border-r border-slate-200 px-1 py-1">
                          {r.ugst}
                          <div className="text-[90%] opacity-80">{r.ugstPct}</div>
                        </td>
                      ) : null}
                      <td className="px-1 py-1">{r.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {config.other.remarks_body.trim() ? (
              <div className="mt-2 rounded border border-slate-200 bg-slate-50/50 px-2 py-1.5 text-slate-800">
                {config.other.remarks_heading.trim() ? (
                  <p style={labelStyle}>{config.other.remarks_heading}</p>
                ) : null}
                <p className="whitespace-pre-line">{config.other.remarks_body}</p>
              </div>
            ) : null}

            <div className="mt-2 flex flex-col gap-2 border-t border-slate-200 pt-2 sm:flex-row sm:justify-between">
              <div className="max-w-md text-slate-800">
                <p style={labelStyle}>{tot.total_in_words_label}</p>
                <p>{SAMPLE.totalWords}</p>
              </div>
              <div className="text-right text-slate-900">
                <p>
                  {tot.subtotal_label} {SAMPLE.subTotal}
                </p>
                {config.show_tax_breakdown && (
                  <>
                    <p>
                      {SAMPLE.cgstLbl} {SAMPLE.cgstAmt}
                    </p>
                    {tbl.show_second_tax_column ? (
                      <p>
                        {SAMPLE.ugstLbl} {SAMPLE.ugstAmt}
                      </p>
                    ) : null}
                  </>
                )}
                <p className="text-base font-bold" style={{ color: config.primary_color }}>
                  {tot.total_label} {SAMPLE.total}
                </p>
                {paid ? (
                  <>
                    <p className="mt-1 text-sm font-semibold text-emerald-800">
                      {tot.amount_paid_label} {SAMPLE.total}
                    </p>
                    <p className="text-sm font-semibold text-emerald-700">
                      {tot.balance_due_label} {tot.balance_due_zero}
                    </p>
                  </>
                ) : null}
              </div>
            </div>

            <div className="mt-3 grid gap-2 border-t border-slate-200 pt-2 text-[7pt] text-slate-700 sm:grid-cols-2 sm:text-[8pt]">
              <div className="min-w-0">
                {payToBlockSafeHtml(config) ? (
                  <div
                    className="font-sans [&_em]:italic [&_ol]:list-decimal [&_ol]:pl-4 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: payToBlockSafeHtml(config) }}
                  />
                ) : (
                  <p className="italic text-slate-400">
                    Pay-to details will appear here — use General → Pay to &amp; thank you.
                  </p>
                )}
              </div>
              <div className="min-w-0">
                <div
                  className="[&_em]:italic [&_ol]:list-decimal [&_ol]:pl-4 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-4"
                  dangerouslySetInnerHTML={{
                    __html: config.thank_you_message?.trim()
                      ? fragmentToSafeHtml(config.thank_you_message)
                      : escapeHtml('Thank you for your business.'),
                  }}
                />
                <p className="mt-2 font-medium text-slate-900">{config.company_address}</p>
                <p className="mt-1 opacity-90">{config.footer_legal_text}</p>
              </div>
            </div>

            <div
              className="mt-3 border-t border-slate-200 pt-2 text-center"
              style={{
                fontSize: `${config.header_footer.footer.font_size_pt}pt`,
                color: config.header_footer.footer.font_color,
              }}
            >
              {config.header_footer.footer.footer_content_note?.trim() ? (
                <div className="mb-1.5 whitespace-pre-line opacity-95">{config.header_footer.footer.footer_content_note}</div>
              ) : null}
              {config.header_footer.footer.show_page_number && (
                <span className="opacity-80">Page 1 of 1</span>
              )}
            </div>
          </div>
        </div>
        <p className="mt-2 text-center text-[10px] text-slate-500">Preview uses sample invoice data — PDF output in a later phase.</p>
      </div>
    </div>
  );
};
